import React, { useState, useEffect, useCallback } from "react";
import Modal from "./Modal";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { adminApiRequest } from "../utils/adminApi";

const COLORS = ["#14b8a6", "#3b82f6", "#f59e0b", "#ef4444"];

const StatCard = ({
  title,
  value,
  icon,
  accent = "bg-emerald-500",
  valueClass = "text-slate-900",
}) => {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className={`h-1 w-full ${accent}`} />
      <div className="p-5">
        <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50 text-2xl">
          {icon}
        </div>
        <p className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">
          {title}
        </p>
        <p className={`mt-2 text-2xl font-bold tracking-tight ${valueClass}`}>
          {value}
        </p>
      </div>
    </div>
  );
};

const SectionCard = ({ title, children, action }) => {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
        <h2 className="text-base font-semibold text-slate-800">{title}</h2>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
};

const DataTable = ({ headers, rows, emptyText = "No data available" }) => {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-[0.12em] text-slate-500">
            {headers.map((header, index) => (
              <th
                key={header}
                className={`py-3 font-bold ${
                  index === headers.length - 1 ? "pr-0" : "pr-4"
                }`}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length ? (
            rows.map((row, idx) => (
              <tr
                key={idx}
                className={`border-b border-slate-50 last:border-b-0 ${
                  row.isInternal ? "bg-amber-50/80" : ""
                }`}
              >
                {row.cells.map((cell, cellIdx) => (
                  <td
                    key={cellIdx}
                    className={`py-3 ${
                      row.isInternal
                        ? "text-amber-800"
                        : "text-slate-700"
                    } ${cellIdx === row.cells.length - 1 ? "pr-0" : "pr-4"}`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={headers.length}
                className="py-8 text-center text-sm italic text-slate-400"
              >
                {emptyText}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const InternalCustomerBadge = () => (
  <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800">
    Admin
  </span>
);

const formatCustomerName = (cust) =>
  `${cust.first_name || ""} ${cust.last_name || ""}`.trim() || "Unknown Customer";

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const today = new Date();
  const defaultEnd = today.toISOString().slice(0, 10);
  const defaultStart = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);

  const [periodEnd, setPeriodEnd] = useState(defaultEnd);
  const [periodStart, setPeriodStart] = useState(defaultStart);

  const [modalData, setModalData] = useState(null);
  const [modalTitle, setModalTitle] = useState("");

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const data = await adminApiRequest({
        script: "analytics",
        data: {
          start: periodStart,
          end: periodEnd,
        },
      });

      if (!data.success) {
        throw new Error(data.message || "Failed to load analytics");
      }

      setAnalytics(data.analytics);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [periodStart, periodEnd]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center bg-slate-50">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-300 border-t-slate-900" />
      </div>
    );
  }

  if (error) {
    return (
      <p className="p-6 text-sm font-medium text-red-500">Error: {error}</p>
    );
  }

  if (!analytics) {
    return <p className="p-6 text-sm text-slate-500">No data available</p>;
  }

  const {
    ordersSummary = [],
    topItems = [],
    topCustomers = [],
    ordersByType = [],
    totalOrders = 0,
    totalRevenue = 0,
    totalExpenses = 0,
    profitOrLoss = 0,
    newCustomers = 0,
    returningCustomers = 0,
  } = analytics;

  const bestSeller = topItems[0] || null;

 const actualCustomers = topCustomers.filter((cust) => !cust.is_internal);

  const topCustomerValue = actualCustomers[0] || null;

  const topCustomerVolume = actualCustomers.reduce(
    (max, c) => (c.total_orders > (max?.total_orders || 0) ? c : max),
    null,
  );

  const topCustomersByVolume = [...topCustomers].sort(
    (a, b) => b.total_orders - a.total_orders,
  );

  const totalOrderTypeRevenue = ordersByType.reduce(
    (sum, item) => sum + Number(item.revenue || 0),
    0,
  );

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-6 p-2">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Analytics Dashboard
          </h1>

          <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                Start
              </label>
              <input
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-slate-500 focus:ring-4 focus:ring-slate-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                End
              </label>
              <input
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-slate-500 focus:ring-4 focus:ring-slate-100"
              />
            </div>

            <button
              className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
              onClick={fetchAnalytics}
            >
              Apply
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          <StatCard
            title="Total Orders"
            value={totalOrders}
            icon="🧾"
            accent="bg-sky-500"
          />

          <StatCard
            title="Gross Income"
            value={`£${Number(totalRevenue).toFixed(2)}`}
            icon="💰"
            accent="bg-emerald-500"
          />

          <StatCard
            title="Expenses"
            value={`£${Number(totalExpenses).toFixed(2)}`}
            icon="🧾"
            accent="bg-amber-500"
          />

          <StatCard
            title="Profit / Loss"
            value={`£${Number(profitOrLoss).toFixed(2)}`}
            icon={profitOrLoss < 0 ? "📉" : "📈"}
            accent={profitOrLoss < 0 ? "bg-red-500" : "bg-emerald-500"}
            valueClass={profitOrLoss < 0 ? "text-red-500" : "text-emerald-600"}
          />

          <StatCard
            title="Best Seller"
            value={
              totalOrders > 0 && bestSeller?.quantity_sold > 0
                ? bestSeller.item_name
                : "N/A"
            }
            icon="🍽️"
            accent="bg-violet-500"
          />

          <StatCard
            title="Top Customer Value"
            value={
              totalOrders > 0 && topCustomerValue?.total_value > 0
                ? formatCustomerName(topCustomerValue)
                : "N/A"
            }
            icon="👑"
            accent="bg-indigo-500"
          />

          <StatCard
            title="Top Customer Volume"
            value={
              totalOrders > 0 && topCustomerVolume?.total_orders > 0
                ? formatCustomerName(topCustomerVolume)
                : "N/A"
            }
            icon="🏆"
            accent="bg-cyan-500"
          />

          <StatCard
            title="New Customers"
            value={newCustomers}
            icon="🆕"
            accent="bg-pink-500"
          />

          <StatCard
            title="Returning Customers"
            value={returningCustomers}
            icon="🔁"
            accent="bg-orange-500"
          />
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <SectionCard
            title="Orders Summary"
            action={
              ordersSummary.length > 5 ? (
                <button
                  className="rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-200"
                  onClick={() => {
                    setModalData(ordersSummary);
                    setModalTitle("Orders Summary");
                  }}
                >
                  View All
                </button>
              ) : null
            }
          >
            <DataTable
              headers={["Date", "Total Orders", "Total Revenue (£)"]}
              rows={ordersSummary.slice(0, 5).map((row) => ({
                cells: [
                  row.period,
                  row.total_orders,
                  row.total_revenue.toFixed(2),
                ],
              }))}
            />
          </SectionCard>

          <SectionCard
            title="Top Selling Items"
            action={
              topItems.length > 5 ? (
                <button
                  className="rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-200"
                  onClick={() => {
                    setModalData(topItems);
                    setModalTitle("All Top Selling Items");
                  }}
                >
                  View All
                </button>
              ) : null
            }
          >
            <DataTable
              headers={["Item Name", "Quantity Sold", "Total Revenue (£)"]}
              rows={topItems.slice(0, 5).map((item) => ({
                cells: [
                  item.item_name,
                  item.quantity_sold,
                  item.total_revenue.toFixed(2),
                ],
              }))}
            />
          </SectionCard>

          <SectionCard
            title="Top Customer (Value)"
            action={
              topCustomers.length > 5 ? (
                <button
                  className="rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-200"
                  onClick={() => {
                    setModalData(topCustomers);
                    setModalTitle("Top Customers (Value)");
                  }}
                >
                  View All
                </button>
              ) : null
            }
          >
            <DataTable
              headers={["Name", "Phone", "Total Value (£)"]}
              rows={topCustomers.slice(0, 5).map((cust) => ({
                isInternal: cust.is_internal,
                cells: [
                  <div className="flex items-center">
                    <span>{formatCustomerName(cust)}</span>
                    {cust.is_internal ? <InternalCustomerBadge /> : null}
                  </div>,
                  cust.phone || "—",
                  cust.total_value.toFixed(2),
                ],
              }))}
            />
          </SectionCard>

          <SectionCard
            title="Top Customer (Volume)"
            action={
              topCustomersByVolume.length > 5 ? (
                <button
                  className="rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-200"
                  onClick={() => {
                    setModalData(topCustomersByVolume);
                    setModalTitle("Top Customers (Volume)");
                  }}
                >
                  View All
                </button>
              ) : null
            }
          >
            <DataTable
              headers={["Name", "Phone", "Total Orders"]}
              rows={topCustomersByVolume.slice(0, 5).map((cust) => ({
                isInternal: cust.is_internal,
                cells: [
                  <div className="flex items-center">
                    <span>{formatCustomerName(cust)}</span>
                    {cust.is_internal ? <InternalCustomerBadge /> : null}
                  </div>,
                  cust.phone || "—",
                  cust.total_orders,
                ],
              }))}
            />
          </SectionCard>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <div className="xl:col-span-2">
            <SectionCard title="Orders by Type">
              {ordersByType.length > 0 ? (
                <div className="h-[340px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        dataKey="count"
                        data={ordersByType}
                        nameKey="type"
                        outerRadius={110}
                        innerRadius={55}
                        paddingAngle={3}
                        labelLine={false}
                        label={({ type, count }) => `${type}: ${count}`}
                      >
                        {ordersByType.map((entry, index) => (
                          <Cell
                            key={index}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value, name) => [value, name]}
                        contentStyle={{
                          borderRadius: 12,
                          border: "1px solid #e2e8f0",
                          boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="py-12 text-center text-sm text-slate-400">
                  No data available
                </div>
              )}
            </SectionCard>
          </div>

          <SectionCard title="Order Type Revenue">
            <div className="space-y-4">
              {ordersByType.length > 0 ? (
                ordersByType.map((entry, index) => {
                  const percent = totalOrderTypeRevenue
                    ? (
                        (Number(entry.revenue || 0) / totalOrderTypeRevenue) *
                        100
                      ).toFixed(1)
                    : 0;

                  return (
                    <div
                      key={entry.type}
                      className="rounded-xl border border-slate-100 bg-slate-50 p-4"
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span
                            className="inline-block h-3 w-3 rounded-full"
                            style={{
                              backgroundColor: COLORS[index % COLORS.length],
                            }}
                          />
                          <span className="font-medium capitalize text-slate-700">
                            {entry.type}
                          </span>
                        </div>

                        <span className="text-sm font-semibold text-slate-900">
                          £{Number(entry.revenue || 0).toFixed(2)}
                        </span>
                      </div>

                      <div className="h-2 rounded-full bg-slate-200">
                        <div
                          className="h-2 rounded-full"
                          style={{
                            width: `${percent}%`,
                            backgroundColor: COLORS[index % COLORS.length],
                          }}
                        />
                      </div>

                      <div className="mt-1 text-xs text-slate-500">
                        {percent}% of revenue
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="py-12 text-center text-sm text-slate-400">
                  No data available
                </div>
              )}
            </div>
          </SectionCard>
        </div>

        {modalData && (
          <Modal
            title={modalTitle}
            onClose={() => setModalData(null)}
            showCancel={false}
            showSubmitButton={false}
            className="max-w-5xl"
          >
            <div className="max-h-[70vh] overflow-auto">
              <table className="w-full min-w-[700px] text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-[0.12em] text-slate-500">
                    {modalData[0] &&
                      Object.keys(modalData[0])
                        .filter((key) => key !== "id" && key !== "is_internal")
                        .map((key) => (
                          <th
                            key={key}
                            className="px-0 py-3 pr-4 font-semibold"
                          >
                            {key
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                          </th>
                        ))}
                  </tr>
                </thead>
                <tbody>
                  {modalData.map((row, idx) => (
                    <tr
                      key={idx}
                      className={`border-b border-slate-50 last:border-b-0 ${
                        row.is_internal ? "bg-amber-50/80" : ""
                      }`}
                    >
                      {Object.entries(row)
                        .filter(
                          ([key]) => key !== "id" && key !== "is_internal",
                        )
                        .map(([key, val]) => {
                          let displayValue = val;

                          if (
                            key === "first_name" ||
                            key === "last_name" ||
                            key === "phone"
                          ) {
                            displayValue = val || "—";
                          }

                          if (typeof val === "number" && key.includes("revenue")) {
                            displayValue = val.toFixed(2);
                          }

                          if (typeof val === "number" && key === "total_value") {
                            displayValue = val.toFixed(2);
                          }

                          return (
                            <td
                              key={key}
                              className={`px-0 py-3 pr-4 ${
                                row.is_internal
                                  ? "text-amber-800"
                                  : "text-slate-700"
                              }`}
                            >
                              {displayValue}
                              {key === "last_name" && row.is_internal ? (
                                <InternalCustomerBadge />
                              ) : null}
                            </td>
                          );
                        })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default Analytics;